import { replacePlaceholders } from '../utils/placeholder.utils'
import type {
  AssessmentQuestion,
  AssessmentUserSummary,
} from '../types/assessment.types'
import { Card, CardContent } from '@/components/ui/card'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

interface SurveyQuestionProps {
  question: AssessmentQuestion
  questionNumber: number
  value?: number
  onChange: (questionId: string, value: number) => void
  isEmployee: boolean
  employee: NonNullable<AssessmentUserSummary> | null
  organizationName: string
}

export function SurveyQuestion({
  question,
  questionNumber,
  value,
  onChange,
  isEmployee,
  employee,
  organizationName,
}: SurveyQuestionProps) {
  const rawText = isEmployee ? question.directReportText : question.managerText
  const text = replacePlaceholders(rawText, {
    isEmployee,
    employee,
    organizationName,
  })

  return (
    <Card className="overflow-hidden border-l-2">
      <CardContent className="p-4 space-y-3">
        {/* Question Header */}
        <div className="space-y-1">
          <div className="flex items-start gap-2">
            <span className="font-semibold text-sm text-muted-foreground shrink-0">
              {questionNumber}.
            </span>
            <p className="text-sm flex-1 leading-relaxed">{text}</p>
            <span className="text-destructive text-xs shrink-0">*</span>
          </div>
          <p className="text-xs text-muted-foreground/70 pl-5">
            1 = lowest; 10 = highest
          </p>
        </div>

        {/* Rating Scale */}
        <RadioGroup
          value={value?.toString() ?? ''}
          onValueChange={(val: string) =>
            onChange(question.id, parseInt(val, 10))
          }
          className="flex items-center justify-between sm:justify-center gap-1.5 sm:gap-8 pt-1"
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
            <div
              key={rating}
              className="flex flex-col items-center gap-1 flex-shrink-0"
            >
              <Label
                htmlFor={`${question.id}-${rating}`}
                className={cn(
                  'flex flex-col items-center gap-1 cursor-pointer transition-all',
                  'hover:scale-105',
                )}
              >
                <div
                  className={cn(
                    'relative w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 transition-all',
                    'flex items-center justify-center shrink-0',
                    value === rating
                      ? 'bg-primary border-primary scale-105'
                      : 'border-muted-foreground/20 hover:border-primary/50',
                  )}
                >
                  <RadioGroupItem
                    value={rating.toString()}
                    id={`${question.id}-${rating}`}
                    className="absolute inset-0 size-full rounded-full border-0 bg-transparent opacity-0"
                  />
                  {value === rating && (
                    <div className="w-2 h-2 rounded-full bg-white pointer-events-none" />
                  )}
                </div>
                <span
                  className={cn(
                    'text-xs font-medium',
                    value === rating
                      ? isEmployee
                        ? 'text-secondary font-semibold'
                        : 'text-primary font-semibold'
                      : 'text-muted-foreground',
                  )}
                >
                  {rating}
                </span>
              </Label>
            </div>
          ))}
        </RadioGroup>
      </CardContent>
    </Card>
  )
}
